/**
 * useTouchGestures - Touch pan/zoom for SVG elements
 *
 * Returns a viewBox string and event handlers for touch-based pan and pinch-zoom.
 * Attach handlers to the SVG element, use the viewBox for the SVG viewBox attribute.
 *
 * Copyright (c) 2025-2026 Pareidolia LLC. All rights reserved.
 */

import { useCallback, useRef, useState } from 'react';

interface ViewBoxState {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TouchGestureHandlers {
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
}

interface UseTouchGesturesOptions {
  initialWidth: number;
  initialHeight: number;
  minScale?: number;
  maxScale?: number;
}

export function useTouchGestures(options: UseTouchGesturesOptions) {
  const { initialWidth, initialHeight, minScale = 0.5, maxScale = 3 } = options;

  const [viewBox, setViewBox] = useState<ViewBoxState>({
    x: 0,
    y: 0,
    width: initialWidth,
    height: initialHeight,
  });

  const touchStartRef = useRef<{ x: number; y: number; dist: number; viewBox: ViewBoxState } | null>(null);

  const getTouchCenter = (touches: React.TouchList) => ({
    x: (touches[0].clientX + (touches[1]?.clientX ?? touches[0].clientX)) / (touches.length > 1 ? 2 : 1),
    y: (touches[0].clientY + (touches[1]?.clientY ?? touches[0].clientY)) / (touches.length > 1 ? 2 : 1),
  });

  const getTouchDist = (touches: React.TouchList) => {
    if (touches.length < 2) return 0;
    const dx = touches[1].clientX - touches[0].clientX;
    const dy = touches[1].clientY - touches[0].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const center = getTouchCenter(e.touches);
    touchStartRef.current = {
      x: center.x,
      y: center.y,
      dist: getTouchDist(e.touches),
      viewBox: { ...viewBox },
    };
  }, [viewBox]);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    e.preventDefault();
    const start = touchStartRef.current;
    if (!start) return;

    const center = getTouchCenter(e.touches);
    const dx = center.x - start.x;
    const dy = center.y - start.y;

    let newViewBox = { ...start.viewBox };

    // Pan: translate viewBox opposite to finger movement
    const scaleX = start.viewBox.width / initialWidth;
    newViewBox.x = start.viewBox.x - dx * scaleX;
    newViewBox.y = start.viewBox.y - dy * scaleX;

    // Pinch zoom
    if (e.touches.length >= 2 && start.dist > 0) {
      const currentDist = getTouchDist(e.touches);
      const ratio = start.dist / currentDist;
      const newWidth = Math.max(initialWidth * minScale, Math.min(initialWidth * maxScale, start.viewBox.width * ratio));
      const newHeight = Math.max(initialHeight * minScale, Math.min(initialHeight * maxScale, start.viewBox.height * ratio));

      // Zoom around center
      const cx = start.viewBox.x + start.viewBox.width / 2;
      const cy = start.viewBox.y + start.viewBox.height / 2;
      newViewBox = {
        x: cx - newWidth / 2,
        y: cy - newHeight / 2,
        width: newWidth,
        height: newHeight,
      };
    }

    setViewBox(newViewBox);
  }, [initialWidth, initialHeight, minScale, maxScale]);

  const onTouchEnd = useCallback(() => {
    touchStartRef.current = null;
  }, []);

  const resetView = useCallback(() => {
    setViewBox({ x: 0, y: 0, width: initialWidth, height: initialHeight });
  }, [initialWidth, initialHeight]);

  const viewBoxString = `${viewBox.x} ${viewBox.y} ${viewBox.width} ${viewBox.height}`;

  const handlers: TouchGestureHandlers = { onTouchStart, onTouchMove, onTouchEnd };

  return { viewBox: viewBoxString, viewBoxState: viewBox, handlers, resetView };
}

export type { ViewBoxState, TouchGestureHandlers, UseTouchGesturesOptions };
